import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Button, Card } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { setFilters } from "../creditorPaymentRecordSlice";
import { fetchCreditorPaymentRecord } from "../CreditorPaymentRecordThunk";
import { useDefaultDateRange } from "../../../../hooks/useDefaultDateRange";
import SearchListAutoComplete from "../../../../components/SearchListAutoComplete";
import RecordDateFilter from "../../../../components/filters/RecordDateFilter";
import {  
  fetchLocations,
  fetchSuppliers,
  fetchUsers,
} from "../../../../store/commonThunks/lookups";
import { currentDateEndTime, currentDateStartTime } from "../../../../lib/functions";
import CommonDateRangeFilter from "../../../../components/common/filters/CommonDateRangeFilter";

const CreditorPaymentRecordFilters = ({ messageApi }) => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.creditorPaymentRecord);
  const lookups = useSelector((state) => state.lookups);

  useDefaultDateRange(setFilters, filters, "today");

  // Fetch lookup data on mount
  useEffect(() => {
    dispatch(fetchSuppliers());
    dispatch(fetchLocations());
    dispatch(fetchUsers());
  }, [dispatch]);

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleReport = () => {
    if (!filters.filterType) {
      messageApi.warning("Select Filter Type");
      return;
    }

    const payload = {
      selectedFilterType: filters.filterType,
      selectedRecordType: filters.recordType,
      filters: {
        ...filters,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      }
    };

    dispatch(fetchCreditorPaymentRecord(payload)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        messageApi.success("Report loaded ✅");
      } else {
        messageApi.error("Failed to fetch records ❌");
      }
    });
  };

  const dynamicSpan = 4;

  const renderExtraField = () => {
    switch (filters.filterType) {
      case "By Supplier":
        return (
          <Col xs={24} sm={12} md={12} lg={8} xl={6}>
            <label>Search Supplier</label>
            <SearchListAutoComplete
              entityType="supplier"
              optionsList={lookups.suppliers
                .filter(c => c.acc_type_id === "creditor")
                .map((c) => ({
                  sale_id: c.acc_id,
                  display_text: c.acc_name,
                  institution_name: c.institution_name,
                  address: c.address,
                  contact_no: c.contact_no,
                }))}
              onSelect={(obj) => handleFilterChange("supplierId", obj?.sale_id)}
              placeholder="Search Supplier"
              showAddNew={false}
            />
          </Col>
        );

      case "By Location":
        return (
          <Col xs={24} sm={12} md={12} lg={8} xl={6}>
            <label>Search Location</label>
            <SearchListAutoComplete
              optionsList={lookups.locations.map((loc) => ({
                sale_id: loc.location_id,
                display_text: loc.location_name,
              }))}
              onSelect={(obj) => handleFilterChange("locationId", obj?.sale_id)}
              placeholder="Location"
              showAddNew={false}
            />
          </Col>
        );

      case "By User":
        return (
          <Col xs={24} sm={12} md={12} lg={8} xl={6}>
            <label>Search User</label>
            <SearchListAutoComplete
              entityType="user"
              optionsList={lookups.users.map((u) => ({
                sale_id: u.id,
                display_text: u.full_name || u.name,
              }))}
              onSelect={(obj) => handleFilterChange("userId", obj?.sale_id)}
              placeholder="User"
              showAddNew={false}
            />
          </Col>
        );

      default:
        return null;
    }
  };

  return (
    <Card className="mb-4 shadow-md rounded-xl">
      <Row gutter={[16, 16]} style={{ alignItems: "center" }}>
        <Col xs={24} sm={12} md={12} lg={8} xl={6}>
          <label>Filter Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "All", display_text: "All" },
              { sale_id: "By Supplier", display_text: "By Supplier" },
              { sale_id: "By Location", display_text: "By Location" },
              { sale_id: "By User", display_text: "By User" },
            ]}
            value={{
              value: filters.filterType,
              label: filters.filterType,
            }}
            onSelect={(obj) => handleFilterChange("filterType", obj?.sale_id)}
            placeholder="Filter Type"
            showAddNew={false}
          />
        </Col>

        {renderExtraField()}

        <Col xs={24} sm={12} md={12} lg={8} xl={6}>
          <label>Record Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "without details", display_text: "Without Details" },
              { sale_id: "with details", display_text: "With Details" },
            ]}
            value={{
              value: filters.recordType,
              label: filters.recordType === "with details" ? "With Details" : "Without Details",
            }}
            onSelect={(obj) => handleFilterChange("recordType", obj?.sale_id)}
            placeholder="Record Type"
            showAddNew={false}
          />
        </Col>

        <CommonDateRangeFilter
          filters={filters}
          onChange={(fromDate, toDate) => {
          handleFilterChange("fromDate", fromDate);
          handleFilterChange("toDate", toDate);
          }}
        />

        <Col xs={24} sm={12} md={12} lg={8} xl={6} style={{ display: "flex", alignItems: "flex-end", height: 58 }}>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleReport}
            block
            style={{height: 35.19}}
            data-testid="report-btn"
          >
            Report
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

export default CreditorPaymentRecordFilters;